import {
    PlayCircleIcon,
    PauseCircleIcon,
    StopCircleIcon,
    MinusCircleIcon
} from '@heroicons/react/24/solid';

export interface MonitoringMeta {
    Icon: typeof PlayCircleIcon;
    iconColor: string;
    arcColor: string;
    label: string;
}

// DM's enum is unstarted | started | paused | stopped (capitalized in the DB mirror);
// mirrors the DM monitoring mapping in the patient files Actions tab.
function monitoringMeta(status: string): MonitoringMeta {
    switch (status) {
        case 'started':
            return {
                Icon: PlayCircleIcon,
                iconColor: 'text-green-500',
                arcColor: 'text-emerald-500',
                label: 'Monitoring active'
            };
        case 'paused':
            return {
                Icon: PauseCircleIcon,
                iconColor: 'text-amber-500',
                arcColor: 'text-amber-500',
                label: 'Monitoring paused'
            };
        case 'stopped':
            return {
                Icon: StopCircleIcon,
                iconColor: 'text-red-500',
                arcColor: 'text-red-500',
                label: 'Monitoring stopped'
            };
        default:
            return {
                Icon: MinusCircleIcon,
                iconColor: 'text-gray-300',
                arcColor: 'text-gray-300',
                label: 'Monitoring not started'
            };
    }
}

interface AlignerMonitoringState {
    meta: MonitoringMeta;
    isMonitoring: boolean;
    current: number;
    total: number;
    tooltip: string;
}

export function alignerMonitoringState(
    current: number | null,
    total: number | null,
    monitoringStatus: string | null
): AlignerMonitoringState {
    const raw = (monitoringStatus ?? '').toLowerCase();
    // 'Resumed' is Monday-speak for an active monitoring (written by the v1
    // resume action and the Monday->DB sync): render it exactly like started.
    const status = raw === 'resumed' ? 'started' : raw;
    const isMonitoring = status === 'started' || status === 'paused' || status === 'stopped';
    const meta = monitoringMeta(isMonitoring ? status : '');

    const safeTotal = total && total > 0 ? total : 0;
    const safeCurrent = current && current > 0 ? Math.min(current, safeTotal || current) : 0;
    const tooltip = isMonitoring
        ? `Aligner ${safeCurrent} of ${safeTotal} · ${meta.label}`
        : meta.label;
    return { meta, isMonitoring, current: safeCurrent, total: safeTotal, tooltip };
}
